/**
 * Zbiorcza nieobecność z panelu (Bronek 2026-10-14: „Czasem gdy tablet zawiedzie chłopaki piszą na kartkach").
 * Czyste reguły okna „Oznacz nieobecność" — LUSTRO backendu (utils/bulkAbsence.js), który i tak decyduje
 * ostatecznie (400 dla złej daty, lokal pominięty z powodem). Tu: granice pola daty i podgląd, co się stanie
 * z zaznaczonymi lokalami, zanim admin kliknie.
 */
import { STATUS_LABELS } from '../ds/StatusMap';
import { NO_RECORD_VERSION } from './recordConflict';
import { lokaleLabel } from './plural';

/** Lustro backendu ABSENCE_MAX_DAYS_BACK (= tablet, PR #417). */
export const ABSENCE_MAX_DAYS_BACK = 90;
/** Statusy po wizycie, w której ktoś był — takich lokali zbiorczo nie oznaczamy (backend VISIT_RESULT_STATUSES). */
export const VISIT_RESULT_STATUSES: readonly string[] = ['brak_uwag', 'zalecenie', 'odmowa_lokatora', 'odmowa_wpisu'];

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Dzisiejszy dzień 'YYYY-MM-DD' wg zegara przeglądarki (jak „dziś" na tablecie). */
export function todayLocalDay(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** 'YYYY-MM-DD' → 'DD.MM.RRRR' (bez konstruktora Date — bez przesunięć stref). */
export function formatDayPl(day: string | null | undefined): string {
  const m = DAY_RE.exec(String(day || '').slice(0, 10));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(day || '');
}

function shiftDay(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Najwcześniejsza data terminu przeglądu (`inspection.terms`, pozycje bez daty pomijane) albo null. */
export function earliestTermDay(terms: readonly { date?: string | null }[] | null | undefined): string | null {
  const days = (terms || [])
    .map((t) => String((t && t.date) || '').slice(0, 10))
    .filter((d) => DAY_RE.test(d))
    .sort();
  return days.length ? days[0] : null;
}

/** Najwcześniejszy dozwolony dzień nieobecności (`min` pola daty). */
export function absenceMinDay(today: string, firstTermDay?: string | null): string {
  if (firstTermDay && firstTermDay <= today) return firstTermDay;
  if (firstTermDay) return today;
  return shiftDay(today, -ABSENCE_MAX_DAYS_BACK);
}

/** Komunikat pod polem daty albo null — te same granice i treści co backend. */
export function absenceDayError(day: string, today: string, firstTermDay?: string | null): string | null {
  const v = String(day || '').trim();
  const m = DAY_RE.exec(v);
  if (!m) return 'Podaj dzień nieobecności w formacie RRRR-MM-DD.';
  const probe = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (probe.getUTCFullYear() !== Number(m[1]) || probe.getUTCMonth() !== Number(m[2]) - 1 || probe.getUTCDate() !== Number(m[3])) {
    return 'Taka data nie istnieje.';
  }
  if (v > today) return 'Dzień nieobecności nie może być z przyszłości.';
  const min = absenceMinDay(today, firstTermDay);
  if (v >= min) return null;
  if (firstTermDay && firstTermDay <= today) {
    return `Nieobecność nie może być sprzed pierwszego terminu tego przeglądu (${formatDayPl(firstTermDay)}) — sprawdź datę.`;
  }
  if (firstTermDay) {
    return `Pierwszy termin tego przeglądu to ${formatDayPl(firstTermDay)} — przed nim nieobecność można zapisać tylko z dzisiejszą datą.`;
  }
  return `Nieobecność może być najwyżej ${ABSENCE_MAX_DAYS_BACK} dni wstecz (najwcześniej ${formatDayPl(min)}) — sprawdź rok.`;
}

/** Rekord lokalu z GET /apartment-inspections/inspection/:id — pola potrzebne podglądowi. */
export interface BulkAbsenceRecordLike {
  apartmentId?: string | null;
  inspectionStatus?: string | null;
  inspectionDate?: string | null;
  attempts?: { status?: string | null; date?: string | null }[] | null;
  updatedAt?: string | null;
}

export interface BulkAbsencePreviewRow {
  apartmentId: string;
  apartmentNumber: string;
  /** Wersja widziana w oknie (`expectedUpdatedAt`); bez rekordu — NO_RECORD_VERSION (jak formularz lokalu). */
  expectedUpdatedAt: string;
  /** Powód, dla którego lokal zostanie pominięty, albo null (zostanie oznaczony). */
  skipReason: string | null;
}

/**
 * Podgląd dla zaznaczonych lokali: lokal z wynikiem kontroli i lokal z tą samą nieobecnością zostaną
 * pominięte (ta sama reguła co backend planBulkAbsence — serwer sprawdza jeszcze raz na stanie z chwili zapisu).
 */
export function previewBulkAbsence(
  apartments: readonly { id: string; apartmentNumber: string }[],
  records: readonly BulkAbsenceRecordLike[] | null | undefined,
  day: string,
): BulkAbsencePreviewRow[] {
  const byApt = new Map((records || []).filter((r) => r && r.apartmentId).map((r) => [String(r.apartmentId), r]));
  return apartments.map((a) => {
    const rec = byApt.get(a.id) || null;
    let skipReason: string | null = null;
    const status = rec?.inspectionStatus || '';
    if (rec && VISIT_RESULT_STATUSES.includes(status)) {
      const when = rec.inspectionDate ? ` z dnia ${formatDayPl(rec.inspectionDate)}` : '';
      skipReason = `ma wynik kontroli „${STATUS_LABELS[status] || status}"${when}`;
    } else if (rec && (rec.attempts || []).some((x) => x && x.status === 'brak_dostepu' && String(x.date || '').slice(0, 10) === day)) {
      skipReason = `nieobecność z dnia ${formatDayPl(day)} jest już w historii`;
    }
    return {
      apartmentId: a.id,
      apartmentNumber: a.apartmentNumber,
      expectedUpdatedAt: (rec && rec.updatedAt) || NO_RECORD_VERSION,
      skipReason,
    };
  });
}

/** Odpowiedź POST /apartment-inspections/bulk-absence (services/api.ts BulkAbsenceResult). */
export interface BulkAbsenceResultLike {
  marked: { apartmentNumber: string; connectedDefault?: boolean }[];
  skipped: { apartmentNumber: string | null; reason: string }[];
}

/** Nagłówek podsumowania: „Oznaczono nieobecność: 3 lokale. Pominięto: 1 lokal." */
export function bulkAbsenceSummary(result: BulkAbsenceResultLike): string {
  const head = `Oznaczono nieobecność: ${lokaleLabel(result.marked.length)}.`;
  return result.skipped.length ? `${head} Pominięto: ${lokaleLabel(result.skipped.length)}.` : head;
}

/** Numery lokali posortowane jak lista lokali (numerycznie, potem tekstowo). */
export function sortApartmentNumbers<T extends { apartmentNumber: string | null }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => String(a.apartmentNumber ?? '').localeCompare(String(b.apartmentNumber ?? ''), 'pl', { numeric: true }));
}
